/**
 * ExecutiveKpiRow — the six executive figures of card 2254.
 *
 * 🔴 THE CAPTION DOES NOT SCOPE THESE TILES, AND ITS OLD POSITION WAS THE ONLY THING
 *    SAYING OTHERWISE (card 3658). Card 2457 criterion 2 asked for a caption above the
 *    *money tiles*; what shipped sat above all six, and its own header comment then
 *    described that as the intent. Measured on the live endpoint 2026-08-29: the caption's
 *    population reads $7,105.41 / 83.04% while the tiles read $6,765.11 / 77.08%, out of
 *    ONE response — and `totalMembers`/`newMembers` are counts with no First Order Value
 *    counterpart at all. ⇒ The caption correctly scopes ZERO of the six, so moving it down
 *    onto the four money tiles would have narrowed the false claim rather than removed it.
 *    `formatOtherPopulationNotice` states the boundary instead; see web/utils/executiveScope.js.
 *
 * ✅ CARD 4310 FINISHES THAT: `ExecutiveFirstOrderValueCaption` is still declared and EXPORTED
 *    here (it is this file's payload block that feeds it), but this row no longer RENDERS it.
 *    `ExecutiveCharts.jsx` mounts it inside the chart card, next to the first-order series it
 *    actually describes. ⛔ Do not re-add it above the tiles — that is the exact defect above.
 *
 * Presentational and pure: it renders the `data` object of
 * `GET /admin/analytics/executive` (card 2253) and derives nothing except display
 * formatting. The fetch lives in `AdminDashboard.jsx` so that card 2255's single
 * period control can drive this row and the executive charts from one state.
 *
 * 🔴 TWO SHAPES OF THIS PAYLOAD ARE EASY TO RENDER AS A LIE:
 *   · `profitMargin` is a FRACTION (0.7718…), not a percent. Printing it raw
 *     shows "0.77%" for a 77.2% margin — a plausible-looking, wrong business fact.
 *   · `Number(null)` is `0` and `Number.isFinite(0)` is `true`, so the obvious
 *     numeric guard silently turns "not loaded yet" into a confident "$0.00".
 *     `toFiniteOrNull` keeps absent and zero apart; absent renders as an em dash.
 * ⛔ Do not substitute 0 for a missing figure. A zero profit reads as a real
 *    business result, and a zero gross revenue produces a nonsense margin.
 */

import { formatOtherPopulationNotice } from '../../utils/executiveScope';
import {
  formatKpiLabelForPeriod,
  isPeriodScopedKpi,
  resolveExecutivePanelState,
  resolveKpiPeriodKey
} from '../../utils/executiveChartModel';
import styles from './ExecutiveKpiRow.module.css';

/** What a figure looks like when it is genuinely absent (not zero). */
export const EMPTY_VALUE = '—';

const ArrowUp = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M12 19V5M5 12l7-7 7 7" />
  </svg>
);

/**
 * Pure. `null` for anything that is not a finite number, INCLUDING `null`,
 * `undefined` and `''` — all of which `Number()` would happily turn into `0`.
 */
export const toFiniteOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/** Pure. Whole-number count, thousands separated. */
export const formatCount = (value) => {
  const n = toFiniteOrNull(value);
  return n === null ? EMPTY_VALUE : n.toLocaleString('en-US');
};

/** Pure. Dollars, always two decimals. The payload's dollar fields are already dollars. */
export const formatMoney = (value) => {
  const n = toFiniteOrNull(value);
  return n === null
    ? EMPTY_VALUE
    : `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/** Pure. FRACTION → percent. `0.7718…` renders `77.2%`, never `0.77%`. */
export const formatMarginPct = (value) => {
  const n = toFiniteOrNull(value);
  return n === null ? EMPTY_VALUE : `${(n * 100).toFixed(1)}%`;
};

/**
 * Pure. Growth is ALREADY a percent from the endpoint (`57.918…` = +57.9%), so it is
 * NOT multiplied — the opposite convention to `profitMargin`, which is why they are
 * two functions rather than one. Returns `null` when there is no comparison window
 * (`all_time`), so the caller renders no arrow rather than a fabricated 0%.
 */
export const formatGrowthPct = (value) => {
  const n = toFiniteOrNull(value);
  if (n === null) return null;
  return `${n > 0 ? '+' : ''}${n.toFixed(1)}%`;
};

/** Pure. Direction of a growth figure: 'up' | 'down' | 'flat' | null when absent. */
export const deriveGrowthDirection = (value) => {
  const n = toFiniteOrNull(value);
  if (n === null) return null;
  if (n > 0) return 'up';
  if (n < 0) return 'down';
  return 'flat';
};

/**
 * The six figures in render order, exactly as card 2254 specifies them.
 * Pure data — the row maps this list, so order is declared once and not re-stated in JSX.
 */
export const EXECUTIVE_KPI_DEFS = Object.freeze([
  { key: 'totalMembers', label: 'Total Members', format: formatCount, growthKey: 'totalMembersGrowthPct' },
  { key: 'newMembers', label: 'New Members', format: formatCount, growthKey: 'newMembersGrowthPct' },
  { key: 'grossRevenue', label: 'Revenue', format: formatMoney, growthKey: 'revenueGrowthPct' },
  { key: 'totalExpense', label: 'Expenses', format: formatMoney, growthKey: 'expenseGrowthPct' },
  { key: 'profit', label: 'Profit', format: formatMoney, growthKey: 'profitGrowthPct' },
  { key: 'profitMargin', label: 'Profit Margin', format: formatMarginPct }
]);

/**
 * Pure. The scope tail of the caption, derived MECHANICALLY from the payload's own
 * `scope` token (`purchase-day+initial-fulfillment`), never hand-written here.
 * `null` when the payload carries no scope, so the caller renders the bare label
 * rather than a dangling separator.
 */
export const formatFirstOrderValueScope = (scope) => {
  if (typeof scope !== 'string' || scope.trim() === '') return null;
  return scope
    .trim()
    .split('+')
    .map((part) => part.split('-').join(' ').trim())
    .filter((part) => part !== '')
    .join(' + ');
};

/** Punctuation only. Joins the API's label to the API's scope; not part of any copy string. */
const SCOPE_SEPARATOR = '\u2014';

/**
 * Pure. The First Order Value scope caption that sits ABOVE the tiles.
 *
 * CRITICAL - EVERY WORD HERE IS THE API'S. `label`, `basis`, `scope` and `termNote` come from
 * `data.firstOrderValue` on `GET /admin/analytics/executive`; this component owns the
 * layout and the punctuation and nothing else. Hard-coding the copy would let the UI and
 * the endpoint drift into two different definitions of the same business metric, which is
 * exactly the failure this caption exists to prevent.
 *
 * Basis and term note render INLINE and always visible - never hover-only, because hover
 * does not exist at 768px and a scope note nobody can reach is not a scope note.
 *
 * `tileLabels` is what the tiles below actually call themselves, passed in rather than
 * imported back, so the boundary notice cannot drift if a tile is added or renamed —
 * EXECUTIVE_KPI_DEFS stays the single declaration of that list (card 3658).
 */
export const ExecutiveFirstOrderValueCaption = ({ firstOrderValue = null, tileLabels = [] }) => {
  const label = firstOrderValue?.label ?? null;
  const basis = firstOrderValue?.basis ?? null;
  const termNote = firstOrderValue?.termNote ?? null;
  const scope = formatFirstOrderValueScope(firstOrderValue?.scope);
  const otherPopulation = formatOtherPopulationNotice(label, tileLabels);

  if (label === null && basis === null && termNote === null) return null;

  return (
    <div className={styles.scopeCaption} data-testid="executive-first-order-value-caption">
      {label !== null && (
        <p className={styles.scopeLabel} data-testid="executive-first-order-value-label">
          {scope === null ? label : `${label} ${SCOPE_SEPARATOR} ${scope}`}
        </p>
      )}
      {basis !== null && (
        <p className={styles.scopeBasis} data-testid="executive-first-order-value-basis">{basis}</p>
      )}
      {termNote !== null && (
        <p className={styles.scopeTermNote} data-testid="executive-first-order-value-term-note">{termNote}</p>
      )}
      {otherPopulation !== null && (
        <p
          className={styles.scopeOtherPopulation}
          data-testid="executive-first-order-value-other-population"
        >
          {otherPopulation}
        </p>
      )}
    </div>
  );
};

/** Pure. Positive comparison only; flat/down periods deliberately render no chip. */
const GrowthChip = ({ growth }) => {
  const text = formatGrowthPct(growth);
  const direction = deriveGrowthDirection(growth);
  if (text === null || direction !== 'up') return null;

  return (
    <p className={`${styles.growth} ${styles.growthUp}`} data-testid="executive-kpi-growth" data-direction={direction}>
      {direction === 'up' && <ArrowUp />}
      <span>{text}</span>
    </p>
  );
};

/**
 * Pure. One executive figure — or, while its period is in flight, a placeholder standing in
 * for it (card 5133).
 *
 * 🔴 THE EM DASH IS A CLAIM, AND IT IS THE WRONG ONE HERE. `EMPTY_VALUE` asserts that a figure
 *    is genuinely absent; in flight we are asserting nothing at all. So the dash is withheld,
 *    and `data-absent` is withheld with it — a reader that trusts that attribute must not be
 *    told a number is missing when it is merely late. Equally, the number we still hold belongs
 *    to the PREVIOUS period, so printing it would be a confident figure for the wrong window.
 *
 * The placeholder is itself a `.value`, carrying a non-breaking space: it inherits the same
 * font size and line height as a rendered figure, so its line box is identical and the tile
 * cannot change height when the two swap.
 */
const ExecutiveKpiCard = ({ def, metrics, periodKey = null, skeleton = false }) => {
  const raw = metrics?.[def.key];
  const value = def.format(raw);
  const label = isPeriodScopedKpi(def.key) ? formatKpiLabelForPeriod(def.label, periodKey) : def.label;

  return (
    <div
      className={styles.card}
      data-testid={`executive-kpi-${def.key}`}
      data-value={skeleton ? '' : value}
      data-absent={!skeleton && value === EMPTY_VALUE ? 'true' : 'false'}
      data-state={skeleton ? 'skeleton' : 'ready'}
    >
      <p className={styles.label} data-label-base={def.label}>{label}</p>
      {skeleton ? (
        <p
          className={`${styles.value} ${styles.valueSkeleton}`}
          data-testid={`executive-kpi-${def.key}-placeholder`}
          aria-hidden="true"
        >
          &nbsp;
        </p>
      ) : (
        <p className={styles.value}>{value}</p>
      )}
      {def.growthKey && !skeleton && <GrowthChip growth={metrics?.[def.growthKey]} />}
    </div>
  );
};

/**
 * Pure. The row and its six tiles resolve their state through the SAME shared function, so
 * they cannot disagree about whether this period has landed. Referentially transparent, so
 * calling it per use site is the same thing as computing it once.
 */
const isSkeletonPanel = (loading, metrics) =>
  resolveExecutivePanelState({ loading, metrics }) === 'skeleton';

/**
 * The executive KPI row. `metrics` is the `data` object of `/admin/analytics/executive`.
 *
 * `loading` is card 5133. On the FIRST load `metrics` is null and the six tiles would render
 * six em dashes — a claim that the figures are absent, made while they are merely on their way.
 * On a period SWITCH `metrics` still holds the previous period's numbers, so the tiles would
 * instead render six confident figures for a window nobody is looking at any more. Both are
 * wrong in the same way, and one flag removes both: while the request is in flight each tile
 * holds its box and shows a placeholder.
 *
 * `period` is card 5135: the key the period control currently has SELECTED. It is only ever the
 * fallback for the label suffix — see `resolveKpiPeriodKey`, which prefers the key the server
 * echoed with the figures, so a label can never name a window the numbers beneath it do not.
 */
const ExecutiveKpiRow = ({ metrics = null, loading = false, period = null }) => (
  <div
    className={styles.row}
    data-testid="executive-kpi-row"
    data-period={metrics?.period ?? ''}
    data-loading={isSkeletonPanel(loading, metrics) ? 'true' : 'false'}
    aria-busy={isSkeletonPanel(loading, metrics) ? 'true' : 'false'}
  >
    {EXECUTIVE_KPI_DEFS.map((def) => (
      <ExecutiveKpiCard
        key={def.key}
        def={def}
        metrics={metrics}
        periodKey={resolveKpiPeriodKey(metrics, period)}
        skeleton={isSkeletonPanel(loading, metrics)}
      />
    ))}
  </div>
);

export default ExecutiveKpiRow;
